import React, { useRef, useState, useEffect } from 'react';
import { collaborations } from '../mock';
import { Building2, Film, Tv, Award, Briefcase, Radio, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

const Collaborations = ({ showHeader = true }) => {
  const categoryIcons = {
    "Cultural & Public Sector": Building2,
    "Live Events, Experiential & Creative Production": Film,
    "Film, TV & Audio-Visual": Tv,
    "Film Festivals & Markets": Award,
    "Corporate & Brands": Briefcase,
    "Media & Broadcasting": Radio
  };

  const categoryTaglines = {
    "Cultural & Public Sector": "Bringing bold media, film, and arts initiatives to life with cultural and public institutions.",
    "Live Events, Experiential & Creative Production": "Crafting immersive experiences and captivating productions worldwide.",
    "Film, TV & Audio-Visual": "Guiding projects with development, script consulting, and executive production.",
    "Film Festivals & Markets": "Supporting festivals and markets with strategy, programming, and production to promote cinema\nand connect filmmakers, audiences, and industry.",
    "Media & Broadcasting": "Delivering compelling media content and seamless execution, from interviews to full production.",
    "Corporate & Brands": "Delivering strategic campaigns and experiential activations for international brands."
  };

  // Pattern alternation INVERTED per request:
  // 1. Live Events → pattern
  // 2. Film Festivals & Markets → plain
  // 3. Film, TV & Audio-Visual → pattern
  // 4. Cultural & Public Sector → plain
  // 5. Media & Broadcasting → pattern
  // 6. Corporate & Brands → plain
  const categoryBackgrounds = [
    { bg: 'bg-midnight-blue', textColor: 'text-white', iconBg: 'bg-white/10', iconColor: 'text-white', line: 'bg-white/20', hasPattern: true },
    { bg: 'bg-midnight-blue', textColor: 'text-white', iconBg: 'bg-white/10', iconColor: 'text-white', line: 'bg-white/20', hasPattern: false },
    { bg: 'bg-midnight-blue', textColor: 'text-white', iconBg: 'bg-white/10', iconColor: 'text-white', line: 'bg-white/20', hasPattern: true },
    { bg: 'bg-midnight-blue', textColor: 'text-white', iconBg: 'bg-white/10', iconColor: 'text-white', line: 'bg-white/20', hasPattern: false },
    { bg: 'bg-midnight-blue', textColor: 'text-white', iconBg: 'bg-white/10', iconColor: 'text-white', line: 'bg-white/20', hasPattern: true },
    { bg: 'bg-midnight-blue', textColor: 'text-white', iconBg: 'bg-white/10', iconColor: 'text-white', line: 'bg-white/20', hasPattern: false }
  ];

  // Helper function to get flag emoji
  const getCountryFlag = (country) => {
    const countryMap = {
      'United Arab Emirates': '🇦🇪',
      'Saudi Arabia': '🇸🇦',
      'Qatar': '🇶🇦',
      'Lebanon': '🇱🇧',
      'Italy': '🇮🇹',
      'France': '🇫🇷',
      'Switzerland': '🇨🇭',
      'Tunisia': '🇹🇳',
      'Argentina': '🇦🇷',
      'USA': '🇺🇸',
      'UK': '🇬🇧',
      'Canada': '🇨🇦',
      'Spain': '🇪🇸',
      'Portugal': '🇵🇹',
      'Lebanon, MENA': '🇱🇧',
      'USA, UK': '🇺🇸 🇬🇧',
      'Spain, United Arab Emirates': '🇪🇸 🇦🇪',
      'Italy, Saudi Arabia': '🇮🇹 🇸🇦',
      'Italy, United Arab Emirates': '🇮🇹 🇦🇪',
      'France, Saudi Arabia': '🇫🇷 🇸🇦',
      'Lebanon, Dubai': '🇱🇧 🇦🇪',
      'United Arab Emirates, Abu Dhabi': '🇦🇪'
    };
    return countryMap[country] || '';
  };

  // Scroll carousel component
  const ScrollCarousel = ({ clients, colorScheme }) => {
    const scrollContainerRef = useRef(null);
    const [showLeftButton, setShowLeftButton] = useState(false);
    const [showRightButton, setShowRightButton] = useState(true);

    // Check scroll position to show/hide buttons
    const checkScrollPosition = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        
        // Show left button if not at start
        setShowLeftButton(scrollLeft > 10);
        
        // Show right button if not at end (with small threshold)
        setShowRightButton(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    // Add scroll listener
    useEffect(() => {
      const container = scrollContainerRef.current;
      if (container) {
        container.addEventListener('scroll', checkScrollPosition);
        checkScrollPosition(); // Check initial position
        
        return () => {
          container.removeEventListener('scroll', checkScrollPosition);
        };
      }
    }, []);

    const scroll = (direction) => {
      if (scrollContainerRef.current) {
        const scrollAmount = 241; // Card width + gap
        scrollContainerRef.current.scrollBy({
          left: direction === 'left' ? -scrollAmount : scrollAmount,
          behavior: 'smooth'
        });
      }
    };

    // Both backgrounds are dark (midnight-blue and whale-blue/burgundy)
    const isDarkBg = true;

    return (
      <div className="relative group">
        {/* Scroll hint - only show when there's more content to explore (desktop only) */}
        {showRightButton && (
          <div className="hidden sm:block absolute -top-8 right-0 text-xs font-montserrat italic opacity-70 transition-opacity text-white">
            Scroll to explore →
          </div>
        )}

        {/* Left scroll button - white with dark icons */}
        {showLeftButton && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/95 hover:bg-white shadow-xl rounded-full p-2.5 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 border border-pale-blue/30"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-5 h-5 text-midnight-blue" />
          </button>
        )}

        {/* Scrollable container */}
        <div
          ref={scrollContainerRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pt-3 pb-6 snap-x snap-mandatory sm:snap-none pl-[14%] pr-[14%] sm:pl-3 sm:pr-0"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {clients.map((client, clientIdx) => {
            const flag = getCountryFlag(client.country);
            
            // Split countries if there are multiple (separated by comma)
            const countries = client.country.split(',').map(c => c.trim());
            const flags = flag.split(' ').filter(f => f.trim()); // Split flag emojis
            
            const CardContent = (
              <>
                <div className="flex-1 flex flex-col justify-center pt-3 pr-6">
                  <h4 className="font-montserrat font-bold text-base text-white group-hover:text-pale-blue transition-colors duration-300 mb-1.5 leading-snug truncate">
                    {client.name}
                  </h4>
                  {/* Display each country on its own line */}
                  <div className="flex flex-col gap-0.5">
                    {countries.map((country, idx) => (
                      <div key={idx} className="flex items-center gap-1">
                        {flags[idx] && <span className="text-base leading-none">{flags[idx]}</span>}
                        <p className="text-sm text-white/75 group-hover:text-white transition-colors font-medium leading-tight">
                          {country}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
                {client.website && (
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                    <ExternalLink className="w-4 h-4 text-white" />
                  </div>
                )}
              </>
            );

            const cardClasses = "group relative flex-shrink-0 snap-center w-[72vw] max-w-[280px] sm:w-[225px] sm:max-w-none h-[125px] p-4 bg-terracotta/27 hover:bg-terracotta/42 backdrop-blur-sm rounded-lg border-2 border-white/25 hover:border-white/50 shadow-[0_8px_24px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.15)] hover:shadow-[0_16px_36px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.25)] transition-all duration-300 hover:scale-[1.04] hover:-translate-y-0.5 cursor-pointer";

            return client.website ? (
              <a
                key={clientIdx}
                href={client.website}
                target="_blank"
                rel="noopener noreferrer"
                className={cardClasses}
              >
                {CardContent}
              </a>
            ) : (
              <div key={clientIdx} className="group relative flex-shrink-0 snap-center w-[72vw] max-w-[280px] sm:w-[225px] sm:max-w-none h-[125px] p-4 bg-terracotta/27 backdrop-blur-sm rounded-lg border-2 border-white/25 shadow-[0_8px_24px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.15)] transition-all duration-300">
                {CardContent}
              </div>
            );
          })}
        </div>

        {/* Right scroll button - white with dark icons */}
        {showRightButton && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/95 hover:bg-white shadow-xl rounded-full p-2.5 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 border border-pale-blue/30"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-5 h-5 text-midnight-blue" />
          </button>
        )}
      </div>
    );
  };

  return (
    <section id="collaborations" className={`bg-white ${showHeader ? 'py-16' : ''}`}>
      {/* Section Header — small bold centered label (homepage only) */}
      {showHeader && (
        <div className="container mx-auto px-8 md:px-16 mb-12">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-sm tracking-[0.4em] uppercase text-whale-blue font-montserrat font-bold mb-4">
              Clients & Partners
            </h2>
            <p className="text-charcoal/70 font-montserrat text-lg max-w-2xl mx-auto" style={{ whiteSpace: 'pre-line' }}>
              A global network of collaborations across{'\n'}film, media, culture, and live experiences
            </p>
          </div>
        </div>
      )}

      {/* Categories with Horizontal Scroll */}
      <div className="space-y-0">
        {collaborations.map((section, idx) => {
          const Icon = categoryIcons[section.category] || Building2;
          const colorScheme = categoryBackgrounds[idx] || categoryBackgrounds[0];
          const tagline = categoryTaglines[section.category] || "";
          
          return (
            <div key={idx} className={`relative ${colorScheme.bg} py-12 overflow-hidden transition-colors duration-500`}>
              {colorScheme.hasPattern && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 opacity-[0.10]"
                  style={{
                    backgroundImage: 'url(/hero-pattern.png)',
                    backgroundRepeat: 'repeat',
                    backgroundSize: '760px auto',
                    backgroundPosition: 'top left',
                  }}
                />
              )}
              <div className="container mx-auto px-8 md:px-16 relative z-10">
                <div className="max-w-7xl mx-auto">
                  {/* Category Header */}
                  <div className="mb-6">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className={`font-playfair font-bold text-2xl md:text-3xl ${colorScheme.textColor}`}>
                        {section.category}
                      </h3>
                    </div>
                    {tagline && (
                      <p className={`text-sm md:text-base font-montserrat italic ${colorScheme.textColor} opacity-80 max-w-5xl ml-16`} style={{ whiteSpace: 'pre-line' }}>
                        {tagline}
                      </p>
                    )}
                  </div>

                  {/* Horizontal Scrolling Carousel */}
                  <ScrollCarousel clients={section.clients} colorScheme={colorScheme} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Stats Footer */}
      <div className="container mx-auto px-8 md:px-16 mt-20 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="pt-12 border-t-2 border-whale-blue/22">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-playfair font-bold text-whale-blue mb-2">
                  66+
                </div>
                <div className="text-sm text-charcoal/70 font-montserrat">
                  Collaborators
                </div>
              </div>
              <div>
                <div className="text-4xl font-playfair font-bold text-whale-blue mb-2">
                  15+
                </div>
                <div className="text-sm text-charcoal/70 font-montserrat">
                  Countries
                </div>
              </div>
              <div>
                <div className="text-4xl font-playfair font-bold text-whale-blue mb-2">
                  6
                </div>
                <div className="text-sm text-charcoal/70 font-montserrat">
                  Industry Sectors
                </div>
              </div>
              <div>
                <div className="text-4xl font-playfair font-bold text-whale-blue mb-2">
                  15+
                </div>
                <div className="text-sm text-charcoal/70 font-montserrat">
                  Years Experience
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom styles for hiding scrollbar */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default Collaborations;
